import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import type { ReactNode } from 'react';
import { createPortal } from 'react-dom';
import { Link } from 'react-router-dom';
import { AlertCircle, CheckCircle2, Info, X } from 'lucide-react';
import { useT } from '../i18n/index.ts';

/**
 * Global toast notification system for surfacing mutation failures (and
 * the occasional success). Previously each page swallowed mutation
 * errors or surfaced them inline-only; once a form collapses (modal
 * closes, row leaves viewport) the error disappeared with no trace.
 *
 * We do NOT add a dependency — this is a tiny portal-backed stack with
 * DESIGN.md-aligned chrome (`surface-container-highest` base,
 * `error-container` for destructive surfaces, `primary` for success).
 *
 * The provider mounts once under the SessionProvider in App.tsx so the
 * portal outlives any page transition.
 */

export type ToastVariant = 'error' | 'success' | 'info';

/**
 * Optional in-app follow-up rendered as a react-router `<Link>` inside
 * the toast (issue #856). `to` must be an internal SPA path — the link
 * navigates client-side and dismisses its toast on click. Callers that
 * pass no action keep the exact pre-#856 behaviour.
 */
export interface ToastAction {
  readonly label: string;
  readonly to: string;
}

export interface Toast {
  readonly id: string;
  readonly variant: ToastVariant;
  readonly message: string;
  readonly action?: ToastAction;
}

interface ToastOptions {
  readonly action?: ToastAction;
}

interface ToastContextValue {
  readonly toasts: readonly Toast[];
  readonly push: (variant: ToastVariant, message: string, options?: ToastOptions) => string;
  readonly dismiss: (id: string) => void;
  readonly error: (message: string, options?: ToastOptions) => string;
  readonly success: (message: string, options?: ToastOptions) => string;
  readonly info: (message: string, options?: ToastOptions) => string;
}

const ToastContext = createContext<ToastContextValue | null>(null);

const DEFAULT_TTL_MS = 6000;

interface ProviderProps {
  readonly children: ReactNode;
  readonly ttlMs?: number;
}

export function ToastProvider({ children, ttlMs = DEFAULT_TTL_MS }: ProviderProps): JSX.Element {
  const [toasts, setToasts] = useState<readonly Toast[]>([]);
  const timers = useRef<Map<string, ReturnType<typeof setTimeout>>>(new Map());

  const dismiss = useCallback((id: string): void => {
    const handle = timers.current.get(id);
    if (handle !== undefined) {
      clearTimeout(handle);
      timers.current.delete(id);
    }
    setToasts((prev) => prev.filter((t) => t.id !== id));
  }, []);

  const push = useCallback(
    (variant: ToastVariant, message: string, options?: ToastOptions): string => {
      const id = `toast-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 8)}`;
      setToasts((prev) => [...prev, { id, variant, message, action: options?.action }]);
      const handle = setTimeout(() => dismiss(id), ttlMs);
      timers.current.set(id, handle);
      return id;
    },
    [dismiss, ttlMs],
  );

  const value = useMemo<ToastContextValue>(
    () => ({
      toasts,
      push,
      dismiss,
      error: (message, options) => push('error', message, options),
      success: (message, options) => push('success', message, options),
      info: (message, options) => push('info', message, options),
    }),
    [toasts, push, dismiss],
  );

  useEffect(() => {
    return () => {
      for (const handle of timers.current.values()) {
        clearTimeout(handle);
      }
      timers.current.clear();
    };
  }, []);

  return (
    <ToastContext.Provider value={value}>
      {children}
      <ToastViewport toasts={toasts} onDismiss={dismiss} />
    </ToastContext.Provider>
  );
}

export function useToast(): ToastContextValue {
  const ctx = useContext(ToastContext);
  if (ctx === null) {
    throw new Error('useToast must be used inside <ToastProvider>');
  }
  return ctx;
}

interface ViewportProps {
  readonly toasts: readonly Toast[];
  readonly onDismiss: (id: string) => void;
}

function ToastViewport({ toasts, onDismiss }: ViewportProps): JSX.Element | null {
  const t = useT();
  if (typeof document === 'undefined') {
    return null;
  }
  if (toasts.length === 0) {
    return null;
  }
  return createPortal(
    <div
      role="region"
      aria-label={t('toast.region_aria')}
      data-testid="toast-viewport"
      className="pointer-events-none fixed bottom-6 right-6 z-50 flex max-w-[calc(100vw-48px)] flex-col items-end gap-2 max-md:inset-x-4 max-md:bottom-4 max-md:max-w-none max-md:items-stretch"
    >
      {toasts.map((toast) => (
        <ToastItem key={toast.id} toast={toast} onDismiss={onDismiss} />
      ))}
    </div>,
    document.body,
  );
}

interface ToastItemProps {
  readonly toast: Toast;
  readonly onDismiss: (id: string) => void;
}

function ToastItem({ toast, onDismiss }: ToastItemProps): JSX.Element {
  const t = useT();
  const skin = variantSkin(toast.variant);
  return (
    <div
      role={toast.variant === 'error' ? 'alert' : 'status'}
      data-testid={`toast-${toast.variant}`}
      data-toast-id={toast.id}
      className={`pointer-events-auto flex w-full max-w-sm items-start gap-3 rounded-lg border px-4 py-3 shadow-overlay animate-toast-in max-md:max-w-none ${skin.container}`}
    >
      <skin.glyph aria-hidden="true" className={`h-5 w-5 shrink-0 ${skin.icon}`} />
      <div className="flex min-w-0 flex-1 flex-col gap-1">
        <p className={`m-0 break-words text-sm leading-snug ${skin.text}`}>{toast.message}</p>
        {toast.action !== undefined ? (
          <Link
            to={toast.action.to}
            onClick={() => onDismiss(toast.id)}
            data-testid="toast-action"
            className={`self-start text-sm font-medium leading-snug underline underline-offset-2 transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-signal/40 ${skin.action}`}
          >
            {toast.action.label}
          </Link>
        ) : null}
      </div>
      <button
        type="button"
        onClick={() => onDismiss(toast.id)}
        aria-label={t('toast.dismiss_aria')}
        data-testid="toast-dismiss"
        className={`inline-flex h-6 w-6 shrink-0 items-center justify-center rounded-lg transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-signal/40 focus-visible:ring-offset-2 ${skin.dismiss}`}
      >
        <X aria-hidden="true" className="h-4 w-4" />
      </button>
    </div>
  );
}

interface Skin {
  readonly container: string;
  readonly icon: string;
  readonly text: string;
  readonly glyph: typeof AlertCircle;
  readonly dismiss: string;
  /** Action-link tint; underline is structural so color alone never carries meaning. */
  readonly action: string;
}

/**
 * Dispatch Deck toast skins (epic #835): raised panel + tone border +
 * lamp-colored icon. Error stays a dim vermilion surface (readable
 * long-form text), not a saturated slab.
 */
function variantSkin(variant: ToastVariant): Skin {
  switch (variant) {
    case 'error':
      return {
        container: 'border-bad-line bg-bad-dim text-bad-bright',
        icon: 'text-bad',
        text: 'text-bad-bright',
        glyph: AlertCircle,
        dismiss: 'text-bad-bright hover:bg-bad-line/50 focus-visible:ring-offset-bad-dim',
        action: 'text-bad-bright hover:text-paper focus-visible:ring-offset-bad-dim',
      };
    case 'success':
      return {
        container: 'border-ok-line bg-raised text-paper',
        icon: 'text-ok',
        text: 'text-paper',
        glyph: CheckCircle2,
        dismiss: 'text-paper-low hover:bg-active hover:text-paper focus-visible:ring-offset-raised',
        action: 'text-signal hover:text-signal-bright focus-visible:ring-offset-raised',
      };
    default:
      return {
        container: 'border-line bg-raised text-paper',
        icon: 'text-signal',
        text: 'text-paper',
        glyph: Info,
        dismiss: 'text-paper-low hover:bg-active hover:text-paper focus-visible:ring-offset-raised',
        action: 'text-signal hover:text-signal-bright focus-visible:ring-offset-raised',
      };
  }
}

/**
 * Turn any thrown value into a UI-friendly string. Exposed so callers
 * don't have to re-implement the OpsApiError check each time.
 */
export function coerceErrorMessage(
  err: unknown,
  fallback = 'Network error — please try again',
): string {
  if (err === null || err === undefined) {
    return fallback;
  }
  if (err instanceof Error) {
    const codeHolder = err as Error & { readonly code?: string };
    const code = typeof codeHolder.code === 'string' ? codeHolder.code : null;
    return code !== null && code.length > 0 ? `${code}: ${err.message}` : err.message;
  }
  if (typeof err === 'string') {
    return err.length > 0 ? err : fallback;
  }
  return fallback;
}
